<template>
  <div class="product-order-selector">
    <!-- Available Products -->
    <div class="products-section">
      <label class="section-label">
        Productos Disponibles
        <span v-if="availableProducts.length > 0" class="badge badge-secondary">{{ availableProducts.length }}</span>
      </label>
      <div class="search-box" v-if="availableProducts.length > 5">
        <i class="fas fa-search search-icon"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Buscar productos..."
          class="search-input"
        />
      </div>
      <div class="available-products">
        <div
          v-for="product in filteredAvailableProducts"
          :key="'available-' + product.id"
          class="product-item available"
          @click="addProduct(product)"
        >
          <span class="product-name">{{ product.label }}</span>
          <i class="fas fa-plus-circle add-icon"></i>
        </div>
        <div v-if="filteredAvailableProducts.length === 0 && searchQuery" class="empty-state">
          <i class="fas fa-search"></i>
          No se encontraron productos
        </div>
        <div v-else-if="availableProducts.length === 0" class="empty-state">
          Todos los productos han sido seleccionados
        </div>
      </div>
    </div>

    <!-- Selected Products (Orderable) -->
    <div class="products-section">
      <label class="section-label">
        Productos Seleccionados
        <span v-if="selectedProducts.length > 0" class="badge badge-primary">{{ selectedProducts.length }}</span>
        <small class="text-muted">Arrastra para ordenar</small>
      </label>
      <div class="selected-products">
        <transition-group name="list" tag="div">
          <div
            v-for="(product, index) in selectedProducts"
            :key="'selected-' + product.id"
            class="product-item selected"
            draggable="true"
            @dragstart="onDragStart(index)"
            @dragover.prevent="onDragOver(index)"
            @dragend="onDragEnd"
            @drop="onDrop(index)"
            :class="{ 'dragging': draggedIndex === index, 'drag-over': dragOverIndex === index }"
          >
            <div class="drag-handle">
              <i class="fas fa-grip-vertical"></i>
            </div>
            <div class="product-content">
              <span class="product-number">{{ index + 1 }}.</span>
              <span class="product-name">{{ product.label }}</span>
            </div>
            <button
              type="button"
              class="remove-btn"
              @click="removeProduct(index)"
              title="Eliminar producto"
            >
              <i class="fas fa-times-circle"></i>
            </button>
          </div>
        </transition-group>
        <div v-if="selectedProducts.length === 0" class="empty-state">
          <i class="fas fa-arrow-left"></i>
          Selecciona productos de la lista
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductOrderSelector",
  props: {
    products: {
      type: Array,
      required: true,
      // Expected format: [{ id: 'uuid', label: 'Product Name', value: {id: 'uuid'} }]
    },
    value: {
      type: Array,
      default: () => [],
      // Array of product objects: [{id: 'uuid'}]
    },
  },
  data() {
    return {
      draggedIndex: null,
      dragOverIndex: null,
      searchQuery: "",
    };
  },
  computed: {
    selectedProducts() {
      // Map selected entities to full product objects, maintaining order
      return this.value
        .map((entity) => this.products.find((prod) => prod.id === entity.id))
        .filter(Boolean); // Remove any nulls if ID not found
    },
    availableProducts() {
      // Products not yet selected
      const selectedIds = this.value.map(e => e.id);
      return this.products.filter(
        (prod) => !selectedIds.includes(prod.id)
      );
    },
    filteredAvailableProducts() {
      if (!this.searchQuery) {
        return this.availableProducts;
      }
      const query = this.searchQuery.toLowerCase();
      return this.availableProducts.filter(prod =>
        prod.label.toLowerCase().includes(query)
      );
    },
  },
  methods: {
    addProduct(product) {
      const newValue = [...this.value, { id: product.id }];
      this.$emit("input", newValue);
    },
    removeProduct(index) {
      const newValue = [...this.value];
      newValue.splice(index, 1);
      this.$emit("input", newValue);
    },
    onDragStart(index) {
      this.draggedIndex = index;
    },
    onDragOver(index) {
      this.dragOverIndex = index;
    },
    onDrop(dropIndex) {
      if (this.draggedIndex === null) return;

      const newValue = [...this.value];
      const draggedItem = newValue[this.draggedIndex];

      // Remove from old position
      newValue.splice(this.draggedIndex, 1);
      // Insert at new position
      newValue.splice(dropIndex, 0, draggedItem);

      this.$emit("input", newValue);
    },
    onDragEnd() {
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.product-order-selector {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.products-section {
  flex: 1;
  min-width: 280px;
}

.section-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #495057;
  font-size: 0.95rem;

  small {
    font-weight: normal;
    margin-left: 0.5rem;
    font-size: 0.85rem;
  }

  .badge {
    margin-left: 0.5rem;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 10px;
  }

  .badge-secondary {
    background-color: #6c757d;
  }

  .badge-primary {
    background-color: #007bff;
  }
}

.search-box {
  position: relative;
  margin-bottom: 0.75rem;

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
  }

  .search-input {
    width: 100%;
    padding: 0.5rem 0.75rem 0.5rem 2.25rem;
    border: 2px solid #dee2e6;
    border-radius: 0.375rem;
    font-size: 0.9rem;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #80bdff;
    }
  }
}

.available-products,
.selected-products {
  border: 2px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  background-color: #f8f9fa;
}

.product-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  background-color: white;
  border: 1px solid #dee2e6;

  &:last-child {
    margin-bottom: 0;
  }

  .product-name {
    flex: 1;
    font-size: 0.9rem;
  }
}

.product-item.available {
  cursor: pointer;

  &:hover {
    background-color: #e7f5ff;
    border-color: #339af0;
    transform: translateX(4px);

    .add-icon {
      color: #339af0;
    }
  }

  .add-icon {
    color: #28a745;
    font-size: 1.1rem;
    transition: color 0.2s;
  }
}

.product-item.selected {
  cursor: move;
  gap: 0.5rem;

  &:hover {
    background-color: #fff4e6;
    border-color: #fd7e14;
  }

  &.dragging {
    opacity: 0.5;
    transform: rotate(2deg);
  }

  &.drag-over {
    border-top: 3px solid #007bff;
    padding-top: 1rem;
  }

  .drag-handle {
    color: #6c757d;
    cursor: grab;
    font-size: 1rem;

    &:active {
      cursor: grabbing;
    }
  }

  .product-content {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .product-number {
    font-weight: 600;
    color: #6c757d;
    min-width: 28px;
  }

  .remove-btn {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 0.25rem;
    font-size: 1.1rem;
    transition: all 0.2s;

    &:hover {
      color: #c82333;
      transform: scale(1.2);
    }

    &:focus {
      outline: none;
    }
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: #6c757d;
  font-style: italic;
  text-align: center;
  min-height: 100px;

  i {
    margin-right: 0.5rem;
  }
}

// Transition animations
.list-move {
  transition: transform 0.3s ease;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

// Custom scrollbar
.available-products::-webkit-scrollbar,
.selected-products::-webkit-scrollbar {
  width: 8px;
}

.available-products::-webkit-scrollbar-track,
.selected-products::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.available-products::-webkit-scrollbar-thumb,
.selected-products::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;

  &:hover {
    background: #555;
  }
}
</style>
